// Title Studio — live Remotion-player editor for the brand's title
// style specs (the declarative documents the backend Remotion titling
// engine renders; schema in liquidretail_backend/services/
// titleSpecValidator.js). Three surfaces:
//
//   Live preview   → <Player> running the copied composition island
//                    (src/titling/island/, source of truth is
//                    liquidretail_backend/remotion/) with sample meta,
//                    the brand's resolved tokens, and the WORKING spec.
//                    Every control writes the spec immutably, so edits
//                    show up in the player on the very next frame.
//   Refinement     → per-slot knobs (visibility, position, type,
//                    motion) mirroring the validator's vocabulary.
//   AI modify      → natural-language edit via POST
//                    /title-spec/modify (202 + poll — same async-job
//                    shape as StyleOverridesCard's generate/preview).
//
// Save persists only the active format's spec into
// Brand.titleStyleSpec via the standard PATCH; Reset restores the
// last fetched/saved resolved spec for that format.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardBody, VStack, HStack, Flex, Heading, Text, Button, Badge,
  Input, IconButton, Select, Switch, FormLabel, Spinner, Tabs, TabList, Tab,
  Slider, SliderTrack, SliderFilledTrack, SliderThumb,
  NumberInput, NumberInputField, NumberInputStepper,
  NumberIncrementStepper, NumberDecrementStepper,
  SimpleGrid, useToast
} from '@chakra-ui/react';
import { Player } from '@remotion/player';
import { apiJson } from '../auth/apiFetch';
import type { Brand } from '../pages/Brand/types';
import { AIChatPanel } from './AIChatPanel';
// Untyped ESM island copied from the backend (see island/README.md).
import { Canonical } from './island/compositions/Canonical.jsx';
import {
  AdChromeFrame, ChromeSelect, useAdChrome, useChromeIdentity, slugHandle,
  type AdRatio
} from '../components/adChrome';

// ── Types (mirror services/titleSpecValidator.js vocabulary) ────────

type TitleFormat = 'vertical' | 'feed' | 'landscape';

type SlotPosition = {
  anchor: string; align: string;
  offsetX: number; offsetY: number; maxWidthPct: number;
  row?: string | null;
};
type SlotTiming = {
  enterAtSec: number; exitAtSec: number | null;
  enterDurationSec: number; exitDurationSec: number;
};
type SlotTransition = { type: string; direction?: string; spring?: Record<string, number> | null };
type SlotTreatment = {
  scrim: string; scrimOpacity: number; shadow: string; casing: string;
  fontRole: string; weight: number; sizeScale: number; maxLines: number;
  trackingPx: number; colorToken: string;
  [k: string]: unknown;
};
type TitleSpecSlot = {
  key: string; visible: boolean; phase: string;
  position: SlotPosition; timing: SlotTiming;
  transition: SlotTransition; treatment: SlotTreatment;
  [k: string]: unknown;
};
type TitleSpec = { version: number; slots: TitleSpecSlot[]; [k: string]: unknown };

type SpecDoc = Partial<Record<TitleFormat, TitleSpec>>;

type BrandFontToken = {
  family: string; weight?: number; source?: string;
  url?: string | null; fallback?: string;
};
type BrandTokens = { colors: Record<string, string>; fonts: Record<string, BrandFontToken> };

type TitleSpecResponse = {
  titleStyleSpec: SpecDoc | null;
  titleStylePreset: string | null;
  titlingEngine: string;
  brand?: { name: string; handle: string; logoUrl: string | null };
  resolved: Record<TitleFormat, { spec: TitleSpec | null; source: string }>;
  presets: string[];
  tokens: BrandTokens;
  customFonts: unknown[];
};

type StyleResponse = { previewPlate: { url: string; source: string } | null };

// ── Constants ───────────────────────────────────────────────────────

const FORMATS: TitleFormat[] = ['vertical', 'feed', 'landscape'];
const FORMAT_LABEL: Record<TitleFormat, string> = {
  vertical: '9:16 Vertical',
  feed: '4:5 Feed',
  landscape: '16:9 Landscape'
};
// Native canvas dims per format (compositionWidth/Height).
const FORMAT_DIMS: Record<TitleFormat, { w: number; h: number }> = {
  vertical: { w: 1080, h: 1920 },
  feed: { w: 1080, h: 1350 },
  landscape: { w: 1920, h: 1080 }
};
// Same shapes, in the canvas-ratio vocabulary the shared placement
// chrome speaks.
const FORMAT_RATIO: Record<TitleFormat, AdRatio> = {
  vertical: '9:16',
  feed: '4:5',
  landscape: '16:9'
};
const FPS = 24;
const DURATION_FRAMES = 192; // 8s @ 24fps — nominal clip the specs are authored for.
const PREVIEW_MAX_H = 480;   // px cap on the player's rendered height.

const ANCHORS = ['top', 'upperThird', 'center', 'lowerThird', 'bottom'];
const ALIGNS = ['left', 'center', 'right'];
const SHADOWS = ['layered', 'soft', 'none'];
const CASINGS = ['none', 'upper', 'title'];
const TRANSITIONS = ['fade', 'slide', 'pop', 'wipe', 'none'];
const DIRECTIONS = ['up', 'down', 'left', 'right'];

// Bindable meta-field whitelists per slot type — mirror
// services/titleSpecValidator.js BINDABLE_*_META_FIELDS. Used by the
// bind-waterfall editor to populate the field-name dropdown per slot.
// Kept as constants (not fetched) so the editor stays offline-tolerant;
// the validator on save is still the source of truth.
const BINDABLE_TEXT_FIELDS = [
  'headline', 'quote', 'quoteSnippet', 'reviewer', 'badgeText', 'brandName',
  'productName', 'productDescription', 'price', 'deliveryLine', 'ctaText',
  'cta', 'promoText', 'brandTagline', 'brandWebsiteDomain', 'reviewsText',
  'likes', 'reviewCount',
];
const BINDABLE_MULTI_FIELDS = ['badges', 'benefits'];
const BINDABLE_IMAGE_FIELDS = ['productImageUrl', 'brandLogoUrl'];

// Slot-type → bindable-field list. Rating is composite (no bind chain).
function bindableFieldsForSlot(slot: TitleSpecSlot): string[] {
  const slotType = (slot as { slotType?: string }).slotType;
  if (slotType === 'multi')  return BINDABLE_MULTI_FIELDS;
  if (slotType === 'image')  return BINDABLE_IMAGE_FIELDS;
  if (slotType === 'rating') return [];
  return BINDABLE_TEXT_FIELDS;
}

// Brand.videoSettings.titlePlacementMode — absent/null means canonical.
type PlacementMode = 'canonical' | 'content';
const PLACEMENT_MODES: { value: PlacementMode; label: string }[] = [
  { value: 'canonical', label: 'Canonical (fixed)' },
  { value: 'content',   label: 'Content-aware' }
];
function resolvePlacementMode(brand: Brand): PlacementMode {
  return brand.videoSettings?.titlePlacementMode === 'content' ? 'content' : 'canonical';
}

function deepClone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

// Representative sample meta so every slot has content — mirrors the
// backend's buildPreviewSampleMeta (routes/brand.js) minus theme,
// which only the canvas engine reads.
function buildSampleMeta(brand: Brand): Record<string, unknown> {
  return {
    brandName:          brand.name,
    badgeText:          'Customer Favorite',
    productName:        'Signature Product',
    productDescription: 'Crafted for daily wear. Made with premium materials that last.',
    price:              '$48',
    headline:           brand.tagline || 'Made better.',
    cta:                'SHOP NOW',
    ctaText:            'SHOP NOW',
    quote:              'Highly rated for comfort, durability, and standout style.',
    quoteSnippet:       'Highly rated for comfort and style',
    reviewer:           'Verified customer',
    deliveryLine:       'Ships free — arrives in 2-3 days',
    promoText:          null,
    benefits:           [],
    badges:             [],
    rating:             4.6,
    reviewCount:        128,
    reviewsText:        '128 reviews',
    likes:              572,
    endcardMode:        'product',
    brandTagline:       brand.tagline || null,
    brandWebsiteUrl:    brand.websiteUrl || null,
    brandWebsiteDomain: (brand.websiteUrl || '').replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/.*$/, '') || null,
    // Cloudinary/Brandfetch URLs load directly in the browser Player.
    // brandPill uses brandLogoUrl for the logo variant; the new brandLogo
    // slot binds directly to it too. productImageUrl feeds productImage.
    // Both null-safe — image slots return null on empty URLs, so preview
    // just skips the image cleanly instead of erroring.
    brandLogoUrl:       brand.logoUrl || null,
    productImageUrl:    null,  // no sample product image in Player preview; production render fills this in
  };
}

// ── Target: scope-parameterized wiring ──────────────────────────────
// The card renders identically at any scope (brand | product | ad). The
// TARGET supplies everything scope-specific: the endpoints it loads /
// saves / AI-modifies against, the sample meta it previews with, whether
// the brand-level placement control is shown, and the brand context
// (tokens / chrome identity / plate color) the preview always needs. For
// product/ad scope `brand` is the owning brand. brandTarget() wires the
// brand scope so the Brand-page mount stays byte-identical to before.

export type TitleStudioTarget = {
  scope: 'brand' | 'product' | 'ad';
  entityId: string;
  brand: Brand;
  loadUrl: string;                                       // GET → TitleSpecResponse
  previewPlateUrl: string | null;                        // GET → StyleResponse (null ⇒ brand color only)
  saveUrl: string;
  saveMethod: 'PATCH' | 'POST';
  buildSaveBody: (nextDoc: SpecDoc, format: TitleFormat) => unknown;
  modifyUrl: string;                                     // POST kick (202 + jobId)
  modifyStatusUrl: (jobId: string) => string;            // GET poll
  storageKey: string;                                    // localStorage key prefix for the AI chat panel
  buildMeta: () => Record<string, unknown>;
  placementEditable: boolean;                            // brand-level placement mode control
};

/**
 * Brand-scope wiring — byte-identical to the pre-parameterization card:
 * the same title-spec / style / modify endpoints, the same sample meta,
 * and the brand placement control on. Memoize at the call site so the
 * target is referentially stable across renders (see BrandPage).
 */
export function brandTarget(brand: Brand): TitleStudioTarget {
  return {
    scope: 'brand',
    entityId: brand._id,
    brand,
    loadUrl:         `/api/brand/${brand._id}/title-spec`,
    previewPlateUrl: `/api/brand/${brand._id}/style`,
    saveUrl:         `/api/brand/${brand._id}`,
    saveMethod:      'PATCH',
    buildSaveBody:   (nextDoc) => ({ titleStyleSpec: nextDoc }),
    modifyUrl:       `/api/brand/${brand._id}/title-spec/modify`,
    modifyStatusUrl: (jobId) => `/api/brand/${brand._id}/title-spec/modify/${jobId}`,
    storageKey:      `brand.${brand._id}`,
    buildMeta:       () => buildSampleMeta(brand),
    placementEditable: true,
  };
}

// ── Card ────────────────────────────────────────────────────────────

export function TitleStudioCard({
  target,
  onChanged,
  stableFormatTabs = false,
}: {
  target: TitleStudioTarget;
  onChanged?: () => void;
  // /v2 only. The format tab's accessible name is the format label,
  // so the Preset/Custom badge cannot change it. Legacy /brand leaves
  // this off and the tab markup is unchanged.
  stableFormatTabs?: boolean;
}) {
  const toast = useToast();
  // Same object reference as target.brand across renders (parent state),
  // so existing brand.* reads and effect deps stay value-stable.
  const brand = target.brand;

  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [data, setData]       = useState<TitleSpecResponse | null>(null);
  const [previewPlate, setPreviewPlate] = useState<{ url: string; source: string } | null>(null);

  const [format, setFormat] = useState<TitleFormat>('vertical');
  // Working spec per format — deep-cloned from resolved[format].spec;
  // every edit replaces object refs so the Player re-renders instantly.
  const [specs, setSpecs] = useState<SpecDoc>({});
  // Last fetched/saved effective spec per format — Reset target.
  const [baseline, setBaseline] = useState<SpecDoc>({});
  // The brand's saved titleStyleSpec doc — Save spreads the active
  // format's working spec into it so sibling formats are preserved.
  const [specDoc, setSpecDoc] = useState<SpecDoc>({});
  const [dirty, setDirty] = useState<Partial<Record<TitleFormat, boolean>>>({});

  // Which slot rows are expanded in the Refinement panel. Persisted
  // across format switches for the current session — flipping between
  // vertical/feed/landscape shouldn't collapse an operator's open row
  // if the same slot exists in the new format.
  const [expandedSlots, setExpandedSlots] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  // Ads-Manager-style clear-zone bands over the media (vertical only).
  const [showSafeZones, setShowSafeZones] = useState(false);

  // Brand-level placement mode (canonical = fixed spec positions; content =
  // server may nudge for footage). Persisted on change via brand PATCH.
  const [placementMode, setPlacementMode] = useState<PlacementMode>(() => resolvePlacementMode(brand));
  const [placementSaving, setPlacementSaving] = useState(false);

  // Re-sync when the brand prop changes (route switch / parent refresh).
  useEffect(() => {
    setPlacementMode(resolvePlacementMode(brand));
  }, [brand._id, brand.videoSettings?.titlePlacementMode]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Style is fetched only for the previewPlate; a failure there
      // shouldn't kill the card — the plate falls back to brand color.
      // Scopes without a plate endpoint (previewPlateUrl === null) skip it.
      const [specRes, styleRes] = await Promise.all([
        apiJson<TitleSpecResponse>(target.loadUrl),
        target.previewPlateUrl
          ? apiJson<StyleResponse>(target.previewPlateUrl).catch(() => null)
          : Promise.resolve(null)
      ]);
      setData(specRes);
      setPreviewPlate(styleRes?.previewPlate ?? null);
      setSpecDoc(specRes.titleStyleSpec || {});
      const base: SpecDoc = {};
      const work: SpecDoc = {};
      for (const f of FORMATS) {
        const s = specRes.resolved?.[f]?.spec;
        if (s) {
          base[f] = deepClone(s);
          work[f] = deepClone(s);
        }
      }
      setBaseline(base);
      setSpecs(work);
      setDirty({});
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
    // Key on the URL strings (value-stable), NOT the target object: the
    // parent memoizes the target on the brand object, so a plain brand
    // refresh (same _id, new object) must NOT refetch and clobber unsaved
    // edits — matches the pre-parameterization `[brand._id]` behavior.
  }, [target.loadUrl, target.previewPlateUrl]);

  useEffect(() => { void load(); }, [load]);

  const spec = specs[format] ?? null;

  // Slots split into two groups so the UI can collapse the (larger)
  // hidden-stubs group behind a toggle. Visible slots — the ones that
  // actually render on-screen — are the operator's default view.
  const { visibleSlots, hiddenSlots } = useMemo(() => {
    const all = spec?.slots || [];
    return {
      visibleSlots: all.filter(s => s.visible),
      hiddenSlots:  all.filter(s => !s.visible).sort((a, b) => a.key.localeCompare(b.key)),
    };
  }, [spec]);
  const [showHiddenSlots, setShowHiddenSlots] = useState(false);

  // ── Immutable spec editing ────────────────────────────────────────
  //
  // Every patch is by-key so the same helper drives every row in the
  // slot list. `section: null` patches the top-level slot fields
  // (e.g. { bind, visible }); a section name patches inside that
  // subobject (e.g. { position: { anchor: 'top' } }).

  const patchSlotByKey = useCallback((
    slotKey: string,
    section: 'position' | 'timing' | 'transition' | 'treatment' | null,
    patch: Record<string, unknown>
  ) => {
    setSpecs(prev => {
      const cur = prev[format];
      if (!cur) return prev;
      const slots = cur.slots.map(s => {
        if (s.key !== slotKey) return s;
        if (!section) return { ...s, ...patch };
        return { ...s, [section]: { ...(s[section] as Record<string, unknown>), ...patch } };
      });
      return { ...prev, [format]: { ...cur, slots } };
    });
    setDirty(d => ({ ...d, [format]: true }));
  }, [format]);

  // ── Player input ──────────────────────────────────────────────────

  const sampleMeta = useMemo(() => target.buildMeta(), [target]);
  const inputProps = useMemo(() => ({
    format,
    plate: {
      imageUrl: previewPlate?.url ?? undefined,
      color: brand.primaryColor || '#3D3D3D'
    },
    meta: sampleMeta,
    // Per-format fonts (resolved server-side WITH the spec's own font
    // overrides) win over the brand-level set so the preview matches what
    // production renders for this format.
    tokens: {
      ...(data?.tokens ?? { colors: {}, fonts: {} }),
      fonts: (data as any)?.resolved?.[format]?.fonts ?? data?.tokens?.fonts ?? {}
    },
    spec
  }), [format, previewPlate, brand.primaryColor, sampleMeta, data, spec]);

  const dims = FORMAT_DIMS[format];
  const playerMaxW = Math.round(PREVIEW_MAX_H * (dims.w / dims.h));

  // Placement chrome. The title-spec endpoint resolves the connected IG
  // account's real handle, so prefer it over the shared context's copy;
  // everything else falls back to the active brand.
  const chromeRatio: AdRatio = FORMAT_RATIO[format];
  const chromeIdentity = useChromeIdentity({
    handle:  data?.brand?.handle || slugHandle(brand.name),
    name:    data?.brand?.name || brand.name,
    logoUrl: data?.brand?.logoUrl ?? brand.logoUrl ?? null
  });
  // Scope 'any': this card previews a brand-level TITLING SPEC, not a
  // specific ad, so there is no platformFormat to scope to and every
  // platform's placements are worth checking a title against — Reels and
  // Shorts crop the same vertical spec differently.
  //
  // Consequence, deliberate: the selection is stored under its own scope
  // and is NOT shared with the ad pages any more. It used to be (one
  // ratio-keyed bucket for the whole app), but once Meta and Google both
  // offer placements at the same shape, one shared bucket is precisely
  // what would paint YouTube furniture on a Meta ad.
  const { chrome, setChrome, options: chromeOptions } = useAdChrome(chromeRatio, null, 'any');

  // AI modify — the chat panel (services/AIChatPanel.tsx) owns the full
  // conversation flow (multi-turn history, revert, localStorage). This
  // card just wires the endpoints + a callback that applies a resulting
  // spec to the live preview.

  // ── Save / Reset ──────────────────────────────────────────────────

  const handleSave = async () => {
    if (!spec) return;
    setSaving(true);
    try {
      const nextDoc: SpecDoc = { ...specDoc, [format]: spec };
      await apiJson(target.saveUrl, {
        method: target.saveMethod,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(target.buildSaveBody(nextDoc, format))
      });
      setSpecDoc(nextDoc);
      setBaseline(prev => ({ ...prev, [format]: deepClone(spec) }));
      setDirty(d => ({ ...d, [format]: false }));
      toast({ status: 'success', title: `${FORMAT_LABEL[format]} title style saved` });
    } catch (err) {
      toast({ status: 'error', title: 'Save failed', description: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const base = baseline[format];
    if (!base) return;
    setSpecs(prev => ({ ...prev, [format]: deepClone(base) }));
    setDirty(d => ({ ...d, [format]: false }));
  };

  const handlePlacementChange = async (next: PlacementMode) => {
    if (next === placementMode || placementSaving) return;
    const prev = placementMode;
    setPlacementMode(next);
    setPlacementSaving(true);
    try {
      await apiJson(`/api/brand/${brand._id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          videoSettings: {
            ...(brand.videoSettings || {}),
            titlePlacementMode: next
          }
        })
      });
      toast({
        status: 'success',
        title: next === 'canonical' ? 'Canonical placement saved' : 'Content-aware placement saved',
        description: next === 'canonical'
          ? 'Titles render at the exact positions in the style spec.'
          : 'Renders may nudge position and adapt ink color to the footage.'
      });
      // Refresh the parent's brand so brand.videoSettings.titlePlacementMode
      // doesn't go stale for the rest of the Brand page (mirrors VideoModelCard).
      onChanged?.();
    } catch (err) {
      setPlacementMode(prev);
      toast({ status: 'error', title: 'Placement save failed', description: (err as Error).message });
    } finally {
      setPlacementSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────

  return (
    <Card>
      <CardBody>
        <VStack align="stretch" spacing={4}>
          <HStack justify="space-between" wrap="wrap">
            <VStack align="start" spacing={0}>
              <Heading size="sm" textTransform="uppercase" letterSpacing="0.04em">Title Studio</Heading>
              <Text fontSize="xs" color="brand.muted">
                Live Remotion preview of the brand's title style spec — the declarative document the titling engine renders over every clip. Edits update the player instantly; Save persists per format.
              </Text>
            </VStack>
            {data && (
              <Badge fontSize="9px" colorScheme={data.titlingEngine === 'remotion' ? 'purple' : 'gray'}>
                engine: {data.titlingEngine}
              </Badge>
            )}
          </HStack>

          {loading ? (
            <HStack><Spinner size="sm" /><Text fontSize="sm" color="brand.muted">Loading title spec…</Text></HStack>
          ) : error ? (
            <Text color="red.600" fontSize="sm">Failed to load title spec: {error}</Text>
          ) : !data ? null : (
            <VStack align="stretch" spacing={3}>
              {/* Placement mode — brand-level, independent of format tabs.
                  Only editable at brand scope (product/ad inherit it). */}
              {target.placementEditable && (
              <HStack align="flex-start" spacing={3} wrap="wrap">
                <Box minW="200px">
                  <ControlLabel>Placement</ControlLabel>
                  <Select
                    size="sm"
                    value={placementMode}
                    isDisabled={placementSaving}
                    onChange={e => void handlePlacementChange(e.target.value as PlacementMode)}
                    title="How titles are positioned on rendered ads"
                  >
                    {PLACEMENT_MODES.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </Select>
                </Box>
                <Text fontSize="2xs" color="brand.muted" pt={{ base: 0, sm: 5 }} maxW="420px" flex="1">
                  {placementSaving ? (
                    <HStack as="span" spacing={1} display="inline-flex">
                      <Spinner size="xs" />
                      <span>Saving placement…</span>
                    </HStack>
                  ) : placementMode === 'canonical' ? (
                    'Canonical renders the style-spec positions exactly.'
                  ) : (
                    'Content-aware analyzes the footage and may nudge position and adapt ink color.'
                  )}
                </Text>
              </HStack>
              )}

              {/* Format segmented control */}
              <Tabs
                variant="soft-rounded"
                colorScheme="purple"
                size="sm"
                index={FORMATS.indexOf(format)}
                onChange={(i) => setFormat(FORMATS[i])}
              >
                <TabList>
                  {FORMATS.map(f => (
                    <Tab
                      key={f}
                      {...(stableFormatTabs ? { 'aria-label': FORMAT_LABEL[f] } : {})}
                    >
                      {FORMAT_LABEL[f]}
                      <Badge ml={2} fontSize="2xs" colorScheme={specDoc[f] ? 'purple' : 'gray'}>
                        {specDoc[f] ? 'Custom' : 'Preset'}
                      </Badge>
                    </Tab>
                  ))}
                </TabList>
              </Tabs>

              {!spec ? (
                <Text fontSize="sm" color="red.500">
                  No spec resolved for this format ({data.resolved?.[format]?.source || 'unknown'}).
                </Text>
              ) : (
                <Flex gap={5} direction={{ base: 'column', lg: 'row' }} align="flex-start">
                  {/* Live preview */}
                  <VStack
                    align="stretch"
                    spacing={2}
                    flexShrink={0}
                    w={{ base: '100%', lg: `${playerMaxW}px` }}
                    maxW={`${playerMaxW}px`}
                    mx={{ base: 'auto', lg: 0 }}
                  >
                    <AdChromeFrame
                      chrome={chrome}
                      ratio={chromeRatio}
                      identity={chromeIdentity}
                      density="full"
                      maxWidth={playerMaxW}
                      showSafeZones={showSafeZones}
                      // With no chrome selected, still show the strictest
                      // vertical bands so the toggle stays meaningful over
                      // the bare creative.
                      safeZoneKind={chrome === 'none' && format === 'vertical' ? 'reels' : chrome}
                    >
                      <Player
                        key={format}
                        component={Canonical}
                        inputProps={inputProps}
                        durationInFrames={DURATION_FRAMES}
                        fps={FPS}
                        compositionWidth={dims.w}
                        compositionHeight={dims.h}
                        controls
                        loop
                        autoPlay={false}
                        acknowledgeRemotionLicense
                        style={{ width: '100%', height: '100%' }}
                      />
                    </AdChromeFrame>
                    <HStack spacing={2} justify="center">
                      <Text fontSize="2xs" color="brand.muted" fontFamily="mono" flexShrink={0}>
                        spec: {dirty[format] ? 'edited (unsaved)' : data.resolved?.[format]?.source || '—'}
                        {' · '}plate: {previewPlate?.source || 'brand color'}
                      </Text>
                      <ChromeSelect value={chrome} options={chromeOptions} onChange={setChrome} />
                      {format === 'vertical' && (
                        <HStack spacing={1.5} flexShrink={0}>
                          <FormLabel
                            htmlFor="title-safe-zones"
                            fontSize="2xs"
                            color="brand.muted"
                            mb={0}
                            whiteSpace="nowrap"
                          >
                            Safe zones
                          </FormLabel>
                          <Switch
                            id="title-safe-zones"
                            size="sm"
                            colorScheme="yellow"
                            isChecked={showSafeZones}
                            onChange={e => setShowSafeZones(e.target.checked)}
                            title="Show Ads Manager-style clear-zone guardrails over the media"
                          />
                        </HStack>
                      )}
                    </HStack>
                  </VStack>

                  <VStack align="stretch" spacing={3} flex="1" minW={0}>
                    {/* AI chat — moved above the slot list so operators
                        default to describing changes in natural language;
                        the slot editor becomes the drill-down when they
                        need fine control. Multi-turn conversation with
                        per-turn revert; history persists in localStorage
                        keyed by (target.storageKey, format). Sends
                        baseSpec=current live spec so each turn resumes
                        from what the operator sees. */}
                    <ControlGroup title="AI chat">
                      <AIChatPanel<TitleSpec>
                        modifyUrl={target.modifyUrl}
                        modifyStatusUrl={target.modifyStatusUrl}
                        storageKey={`titleStudio.${target.storageKey}.${format}`}
                        currentState={spec}
                        onStateApplied={(nextSpec) => {
                          const next = deepClone(nextSpec);
                          setSpecs(prev => ({ ...prev, [format]: next }));
                          setDirty(d => ({ ...d, [format]: true }));
                        }}
                        buildRequestBody={({ request, history, baseState }) => ({
                          request,
                          format,
                          history,
                          baseSpec: baseState,
                        })}
                        extractResult={(response) => ({
                          state:   (response.spec as TitleSpec | undefined) ?? null,
                          summary: typeof response.summary === 'string' ? response.summary : null,
                        })}
                        placeholder='Describe the change… (Shift+Enter for newline)'
                        emptyStateExample='e.g. "cascade the badges in a row over 0.6s, then show the product image at 5s"'
                      />
                    </ControlGroup>

                    {/* Slots — active (visible) ones by default. Hidden
                        stubs (all SLOT_KEYS the spec hydrated but the
                        operator hasn't enabled) collapse behind a toggle
                        so the primary view stays focused on what's
                        actually rendering. Each row is independently
                        expandable to reveal position / type / motion /
                        source-waterfall controls. */}
                    <VStack align="stretch" spacing={1}>
                      <HStack justify="space-between">
                        <ControlLabel>Slots</ControlLabel>
                        {(visibleSlots.length + hiddenSlots.length) > 0 && (
                          <Text fontSize="2xs" color="brand.muted">
                            {visibleSlots.length} active · {hiddenSlots.length} hidden
                          </Text>
                        )}
                      </HStack>
                      {(visibleSlots.length + hiddenSlots.length) === 0 ? (
                        <Text fontSize="xs" color="brand.muted" fontStyle="italic" py={2}>
                          No slots in this spec yet — use the AI chat above to add some.
                        </Text>
                      ) : (
                        <VStack align="stretch" spacing={0.5}>
                          {visibleSlots.length === 0 && (
                            <Text fontSize="xs" color="brand.muted" fontStyle="italic" py={1}>
                              No slots are currently active. Show hidden slots to enable one.
                            </Text>
                          )}
                          {visibleSlots.map(s => (
                            <SlotRow
                              key={s.key}
                              slot={s}
                              isExpanded={!!expandedSlots[s.key]}
                              onToggle={() => setExpandedSlots(prev => ({ ...prev, [s.key]: !prev[s.key] }))}
                              onPatch={(section, patch) => patchSlotByKey(s.key, section, patch)}
                            />
                          ))}
                          {hiddenSlots.length > 0 && (
                            <Button
                              size="xs"
                              variant="ghost"
                              alignSelf="flex-start"
                              mt={1}
                              onClick={() => setShowHiddenSlots(v => !v)}
                            >
                              {showHiddenSlots
                                ? `Hide ${hiddenSlots.length} inactive slot${hiddenSlots.length === 1 ? '' : 's'}`
                                : `Show ${hiddenSlots.length} inactive slot${hiddenSlots.length === 1 ? '' : 's'}`}
                            </Button>
                          )}
                          {showHiddenSlots && hiddenSlots.map(s => (
                            <SlotRow
                              key={s.key}
                              slot={s}
                              isExpanded={!!expandedSlots[s.key]}
                              onToggle={() => setExpandedSlots(prev => ({ ...prev, [s.key]: !prev[s.key] }))}
                              onPatch={(section, patch) => patchSlotByKey(s.key, section, patch)}
                            />
                          ))}
                        </VStack>
                      )}
                    </VStack>

                    {/* Save / Reset */}
                    <HStack justify="flex-end" spacing={2} pt={1}>
                      <Button size="sm" variant="ghost" onClick={handleReset} isDisabled={!dirty[format]}>
                        Reset
                      </Button>
                      <Button size="sm" variant="brand" onClick={() => void handleSave()} isLoading={saving} isDisabled={!dirty[format]}>
                        Save {FORMAT_LABEL[format]}
                      </Button>
                    </HStack>
                  </VStack>
                </Flex>
              )}
            </VStack>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
}

// ── Small labeled controls ──────────────────────────────────────────

function ControlLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text fontSize="10px" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em" mb={0.5}>
      {children}
    </Text>
  );
}

function ControlGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box borderWidth="1px" borderRadius="md" borderColor="brand.border" p={3}>
      <Text fontSize="xs" fontWeight="600" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em" mb={2}>
        {title}
      </Text>
      {children}
    </Box>
  );
}

function LabeledSelect({
  label, value, options, onChange, isDisabled
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
  isDisabled?: boolean;
}) {
  return (
    <Box>
      <ControlLabel>{label}</ControlLabel>
      <Select size="sm" value={value} onChange={e => onChange(e.target.value)} isDisabled={isDisabled}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </Select>
    </Box>
  );
}

function LabeledSlider({
  label, value, min, max, step, decimals, onChange
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  decimals: number;
  onChange: (v: number) => void;
}) {
  const safe = typeof value === 'number' && Number.isFinite(value) ? value : min;
  return (
    <Box>
      <HStack justify="space-between" mb={0.5}>
        <ControlLabel>{label}</ControlLabel>
        <Text fontSize="10px" fontFamily="mono" color="brand.ink">{safe.toFixed(decimals)}</Text>
      </HStack>
      <Slider value={safe} min={min} max={max} step={step} onChange={onChange} size="sm" focusThumbOnChange={false}>
        <SliderTrack><SliderFilledTrack bg="rsViolet.400" /></SliderTrack>
        <SliderThumb boxShadow="sm" borderColor="rsViolet.400" borderWidth="1px" />
      </Slider>
    </Box>
  );
}

function LabeledNumber({
  label, value, min, max, step, onChange
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  const safe = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return (
    <Box>
      <ControlLabel>{label}</ControlLabel>
      <NumberInput
        size="sm"
        value={safe}
        min={min}
        max={max}
        step={step}
        onChange={(_, n) => { if (Number.isFinite(n)) onChange(n); }}
      >
        <NumberInputField />
        <NumberInputStepper>
          <NumberIncrementStepper />
          <NumberDecrementStepper />
        </NumberInputStepper>
      </NumberInput>
    </Box>
  );
}

// ── Slot list row ───────────────────────────────────────────────────
//
// One collapsible row per slot in the spec. Header shows chevron,
// label, slot-type badge, and visibility switch. Expanded body shows
// the full editor — position, type, motion controls + the bind
// waterfall.

type PatchSection = 'position' | 'timing' | 'transition' | 'treatment' | null;

function SlotRow({
  slot, isExpanded, onToggle, onPatch,
}: {
  slot: TitleSpecSlot;
  isExpanded: boolean;
  onToggle: () => void;
  onPatch: (section: PatchSection, patch: Record<string, unknown>) => void;
}) {
  const slotType = (slot as { slotType?: string }).slotType || 'text';
  return (
    <Box borderTopWidth="1px" borderColor="brand.border" py={2}>
      <HStack
        spacing={2}
        cursor="pointer"
        onClick={onToggle}
        _hover={{ bg: 'brand.canvas' }}
        borderRadius="md"
        px={1}
        py={0.5}
      >
        <Box
          transform={isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'}
          transition="transform 120ms"
          w="12px" h="12px"
          color="brand.muted"
          fontSize="10px"
          lineHeight="12px"
        >
          ▶
        </Box>
        <Text fontSize="sm" fontWeight="600" color={slot.visible ? 'brand.text' : 'brand.muted'} flex="1">
          {slot.key}
        </Text>
        <Badge fontSize="9px" variant="subtle" colorScheme={slotType === 'text' ? 'gray' : slotType === 'multi' ? 'orange' : slotType === 'image' ? 'blue' : 'purple'}>
          {slotType}
        </Badge>
        <HStack
          spacing={2}
          onClick={e => e.stopPropagation()}   // don't collapse on switch tap
        >
          <Switch
            size="sm"
            colorScheme="purple"
            isChecked={slot.visible}
            onChange={e => onPatch(null, { visible: e.target.checked })}
          />
        </HStack>
      </HStack>

      {isExpanded && (
        <VStack align="stretch" spacing={3} pl={5} pt={2}>
          {/* Position */}
          {slotType !== 'rating' && (
            <ControlGroup title="Position">
              <SimpleGrid columns={2} spacingX={4} spacingY={2}>
                <LabeledSelect
                  label="Anchor" value={slot.position.anchor} options={ANCHORS}
                  onChange={v => onPatch('position', { anchor: v })}
                />
                <LabeledSelect
                  label="Align" value={slot.position.align} options={ALIGNS}
                  onChange={v => onPatch('position', { align: v })}
                />
                <LabeledSlider
                  label="Offset X" value={slot.position.offsetX}
                  min={-0.25} max={0.25} step={0.01} decimals={2}
                  onChange={v => onPatch('position', { offsetX: v })}
                />
                <LabeledSlider
                  label="Offset Y" value={slot.position.offsetY}
                  min={-0.25} max={0.25} step={0.01} decimals={2}
                  onChange={v => onPatch('position', { offsetY: v })}
                />
                <LabeledSlider
                  label="Max width" value={slot.position.maxWidthPct}
                  min={0.2} max={1} step={0.05} decimals={2}
                  onChange={v => onPatch('position', { maxWidthPct: v })}
                />
              </SimpleGrid>
            </ControlGroup>
          )}

          {/* Type */}
          <ControlGroup title="Type">
            <SimpleGrid columns={2} spacingX={4} spacingY={2}>
              <LabeledSlider
                label="Size" value={slot.treatment.sizeScale}
                min={0.5} max={2} step={0.05} decimals={2}
                onChange={v => onPatch('treatment', { sizeScale: v })}
              />
              <LabeledSlider
                label="Weight" value={slot.treatment.weight}
                min={100} max={900} step={100} decimals={0}
                onChange={v => onPatch('treatment', { weight: v })}
              />
              <LabeledSlider
                label="Tracking (px)" value={slot.treatment.trackingPx}
                min={0} max={8} step={0.5} decimals={1}
                onChange={v => onPatch('treatment', { trackingPx: v })}
              />
              <LabeledSelect
                label="Shadow" value={slot.treatment.shadow} options={SHADOWS}
                onChange={v => onPatch('treatment', { shadow: v })}
              />
              <LabeledSelect
                label="Casing" value={slot.treatment.casing} options={CASINGS}
                onChange={v => onPatch('treatment', { casing: v })}
              />
            </SimpleGrid>
          </ControlGroup>

          {/* Motion */}
          <ControlGroup title="Motion">
            <SimpleGrid columns={2} spacingX={4} spacingY={2}>
              <LabeledSelect
                label="Transition" value={slot.transition.type} options={TRANSITIONS}
                onChange={v => onPatch('transition', { type: v })}
              />
              <LabeledSelect
                label="Direction"
                value={slot.transition.direction || 'up'}
                options={DIRECTIONS}
                isDisabled={!['slide', 'wipe'].includes(slot.transition.type)}
                onChange={v => onPatch('transition', { direction: v })}
              />
              <LabeledNumber
                label="Enter at (s)" value={slot.timing.enterAtSec}
                min={0} max={15} step={0.1}
                onChange={v => onPatch('timing', { enterAtSec: v })}
              />
              <LabeledNumber
                label="Enter duration (s)" value={slot.timing.enterDurationSec}
                min={0} max={2} step={0.05}
                onChange={v => onPatch('timing', { enterDurationSec: v })}
              />
            </SimpleGrid>
          </ControlGroup>

          {/* Source waterfall (bind chain) — meta fields tried in order,
              first non-empty wins. A literal fallback always wins if
              reached (typed to match the slot: string / array / URL). */}
          {slotType !== 'rating' && (
            <ControlGroup title="Source waterfall">
              <BindWaterfallEditor
                bind={(slot.bind as BindEntry[]) || []}
                fields={bindableFieldsForSlot(slot)}
                slotType={slotType}
                onChange={next => onPatch(null, { bind: next })}
              />
            </ControlGroup>
          )}
        </VStack>
      )}
    </Box>
  );
}

// ── Bind waterfall editor ───────────────────────────────────────────
//
// Editable list of the slot's source cascade. Each entry is either a
// meta-field name (string) OR a literal fallback ({ literal: <value> }).
// Add/remove/reorder rows; the entry type flips between "field" and
// "literal" via the per-row Select.

type BindEntry = string | { literal: unknown };

function BindWaterfallEditor({
  bind, fields, slotType, onChange,
}: {
  bind: BindEntry[];
  fields: string[];
  slotType: string;
  onChange: (next: BindEntry[]) => void;
}) {
  const update = (idx: number, next: BindEntry) => {
    const arr = [...bind];
    arr[idx] = next;
    onChange(arr);
  };
  const remove = (idx: number) => {
    const arr = [...bind];
    arr.splice(idx, 1);
    onChange(arr);
  };
  const move = (idx: number, dir: -1 | 1) => {
    const to = idx + dir;
    if (to < 0 || to >= bind.length) return;
    const arr = [...bind];
    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    onChange(arr);
  };
  const addField = () => onChange([...bind, fields[0] || '']);
  // Empty-string starter for text/image literals; empty-array for multi.
  const addLiteral = () => onChange([...bind, { literal: slotType === 'multi' ? [] : '' }]);

  return (
    <VStack align="stretch" spacing={1.5}>
      {bind.length === 0 ? (
        <Text fontSize="xs" color="brand.muted" fontStyle="italic">
          No sources — this slot will resolve to null. Add a field or a literal fallback below.
        </Text>
      ) : (
        bind.map((entry, idx) => (
          <BindEntryRow
            key={idx}
            idx={idx}
            total={bind.length}
            entry={entry}
            fields={fields}
            slotType={slotType}
            onUpdate={next => update(idx, next)}
            onUp={() => move(idx, -1)}
            onDown={() => move(idx, 1)}
            onRemove={() => remove(idx)}
          />
        ))
      )}
      <HStack spacing={2} pt={1}>
        <Button size="xs" variant="outline" onClick={addField} isDisabled={fields.length === 0}>
          + Add field
        </Button>
        <Button size="xs" variant="outline" onClick={addLiteral}>
          + Add literal fallback
        </Button>
      </HStack>
    </VStack>
  );
}

function BindEntryRow({
  idx, total, entry, fields, slotType, onUpdate, onUp, onDown, onRemove,
}: {
  idx: number;
  total: number;
  entry: BindEntry;
  fields: string[];
  slotType: string;
  onUpdate: (next: BindEntry) => void;
  onUp: () => void;
  onDown: () => void;
  onRemove: () => void;
}) {
  const isLiteral = typeof entry === 'object' && entry !== null && 'literal' in entry;
  const literalValue = isLiteral ? (entry as { literal: unknown }).literal : null;
  const literalIsArray = Array.isArray(literalValue);

  return (
    <HStack
      spacing={1.5}
      p={1.5}
      borderRadius="md"
      bg="brand.canvas"
      borderWidth="1px"
      borderColor="brand.border"
      align="center"
    >
      <Text fontSize="10px" fontFamily="mono" color="brand.muted" w="16px" textAlign="right">#{idx + 1}</Text>
      <Select
        size="xs"
        w="76px"
        value={isLiteral ? 'literal' : 'field'}
        onChange={e => {
          const next = e.target.value;
          if (next === 'field' && isLiteral)  onUpdate(fields[0] || '');
          if (next === 'literal' && !isLiteral) onUpdate({ literal: slotType === 'multi' ? [] : '' });
        }}
      >
        <option value="field">Field</option>
        <option value="literal">Literal</option>
      </Select>
      {isLiteral ? (
        literalIsArray ? (
          // Multi-slot literal — comma-separated pill list.
          <Input
            size="xs"
            fontFamily="mono"
            placeholder='Comma-separated items, e.g. "Bestseller, Free ship, New"'
            value={(literalValue as unknown[]).map(String).join(', ')}
            onChange={e => onUpdate({
              literal: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
            })}
            flex="1"
          />
        ) : (
          <Input
            size="xs"
            placeholder={slotType === 'image' ? 'https://cdn.example.com/fallback.png' : 'Literal value'}
            value={String(literalValue ?? '')}
            onChange={e => onUpdate({ literal: e.target.value })}
            flex="1"
          />
        )
      ) : (
        <Select
          size="xs"
          value={typeof entry === 'string' ? entry : ''}
          onChange={e => onUpdate(e.target.value)}
          flex="1"
        >
          {fields.map(f => <option key={f} value={f}>{f}</option>)}
        </Select>
      )}
      <HStack spacing={0.5}>
        <IconButton aria-label="move up"   size="xs" variant="ghost" icon={<span>▲</span>} onClick={onUp}   isDisabled={idx === 0} />
        <IconButton aria-label="move down" size="xs" variant="ghost" icon={<span>▼</span>} onClick={onDown} isDisabled={idx === total - 1} />
        <IconButton aria-label="remove"    size="xs" variant="ghost" colorScheme="red" icon={<span>×</span>} onClick={onRemove} />
      </HStack>
    </HStack>
  );
}
